import { html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { HelpLightElement } from "./help-light-element.js";
import { readJsonScript } from "../config.js";

interface CmsLogoutInitialState {
  antiforgery?: { fieldName?: string; requestToken?: string };
}

/**
 * Light-DOM logout control for the internal CMS session, surfaced as the last
 * item in the appbar user menu. Renders a native <form> that posts to
 * /cms/LogOff exactly like the legacy Razor view; the server clears the
 * __Host-cms cookie and redirects home. Self-gates on its server island: when
 * the request carries no valid CMS cookie the server emits no island, so
 * render() returns nothing and the user menu is unchanged. Light DOM is
 * required so the native form submit carries the antiforgery hidden input
 * without shadow-DOM form-association quirks (same rationale as
 * help-jonas-cms-login).
 */
@customElement("help-jonas-cms-logout")
export class HelpJonasCmsLogout extends HelpLightElement {
  @property({ attribute: "config-id" }) accessor configId =
    "cms-logout-initial-state";

  @state() private accessor fieldName = "__RequestVerificationToken";
  @state() private accessor requestToken = "";
  @state() private accessor available = false;

  override connectedCallback(): void {
    super.connectedCallback();
    this.readInitialState();
  }

  private readInitialState(): void {
    try {
      const parsed = JSON.parse(
        readJsonScript(document, this.configId),
      ) as CmsLogoutInitialState;
      if (parsed.antiforgery?.fieldName)
        this.fieldName = parsed.antiforgery.fieldName;
      if (parsed.antiforgery?.requestToken)
        this.requestToken = parsed.antiforgery.requestToken;
      this.available = true;
    } catch {
      // No/invalid island: the request is not CMS-authenticated. Render
      // nothing so the user menu is unchanged; never throw here.
      this.available = false;
    }
  }

  override render(): TemplateResult | typeof nothing {
    if (!this.available) return nothing;
    return html`
      <style>
        help-jonas-cms-logout {
          display: block;
        }
        help-jonas-cms-logout .logout {
          margin: 4px 0 0;
          padding-top: 10px;
          border-top: 1px solid var(--color-foreground-300, #d0d0d0);
        }
        help-jonas-cms-logout .logout-button {
          width: 100%;
          box-sizing: border-box;
          padding: 8px 10px;
          border: 0;
          border-radius: 4px;
          background: transparent;
          color: var(--color-text-on-background-800, #1a1a1a);
          font: inherit;
          text-align: left;
          cursor: pointer;
        }
        help-jonas-cms-logout .logout-button:hover {
          background: var(--color-foreground-100, #f1f1f1);
        }
      </style>
      <form method="post" action="/cms/LogOff" class="logout">
        <input
          type="hidden"
          name=${this.fieldName}
          .value=${this.requestToken}
        />
        <button type="submit" class="logout-button">Uitloggen</button>
      </form>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-cms-logout": HelpJonasCmsLogout;
  }
}
